/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react/jsx-props-no-spreading */
import React, { Suspense } from 'react';
import { BrowserRouter, Switch, Route, Redirect } from 'react-router-dom';
import {
    Spinner,
    SpinnerSize,
    initializeIcons,
    loadTheme,
} from '@fluentui/react';
import { Provider } from 'react-redux';
import {
    LoginCallback,
    SilentLoginCallback,
    LogoutCallback,
    CheckAuthentication,
    GlobalErrorHandler,
    ErrorPage,
    ChannelTypes,
    SecuredRoute,
} from '@eta/components';
import palette from './styles/_colors.scss';
import MyProfile from './Profile/components/MyProfile';
import { AcceptInvitation } from './AcceptInvitation/components/AcceptInvitation';
import store from './common/datastore/store';
import { Layout } from './common/layout';
import { CodeManagement } from './codeManagement/components/CodeManagement';
import { authService } from './Security/Security';
import TaxpayerProfile from './TaxpayerProfile/components/TaxpayerProfile';
import { EnsureNotSlashAtEnd } from './helpers/UIHelpers';
import { AnonymousHome } from './home/components/AnonymousHome';
import Application from './ProviderManagement/Components/Application';
import i18n from './i18n';
import { VerifyIntermediary } from './ProviderManagement/Components/VerifyIntermediary';
import SwitchTaxpayer from './common/components/SwitchTaxpayers';
import ChangePassword from './common/component/ChangePassword';
import SignUp from './SignUp/SignUp';
import DeviceDetails from './TaxpayerProfile/components/POS/DeviceDetails';
import { ClaimsHelper } from './helpers/ClaimsHelper';
import { getBooleanValue } from './helpers/utils';

initializeIcons((window as any).REACT_APP_Fabric_Icons_CDN);;
loadTheme({
    palette,
});
const APP_NAME = 'Registration Portal';
const APP_LOGO = 'logo192.png';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const invoicingPortalURL = (window as any)
    .REACT_APP_InvoicingPortalURL as string;
export const menuItems = [
    {
        id: 'invoicingPortal',
        title: 'menu:invoicePortal',
        pathname: `/invoicePortal`,
        iconName: 'NavigateForwardMirrored',
    },
    {
        id: 'ManageProfile',
        title: 'menu:manageProfile',
        pathname: '',
        iconName: '',
        subItems: [
            {
                id: 'myProfile',
                title: 'menu:myProfile',
                pathname: '/myProfile',
                iconName: 'Contact',
            },
            {
                id: 'TaxpayerProfile',
                title: 'menu:taxpayerProfile',
                pathname: '/TaxpayerProfile',
                iconName: 'EMI',
            },
        ],
    },
    {
        id: 'userGuide',
        title: 'menu:userGuide',
        pathname: '/userguide',
        iconName: 'BookAnswers',
    },
];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const publicPortalUrl = EnsureNotSlashAtEnd(
    (window as any).REACT_APP_PublicGatewayApiBaseURL
);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const appVersion = (window as any).REACT_APP_ReleaseName;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const registerPOSConfig = getBooleanValue(
    (window as any).REACT_APP_RegisterPOS
);
function App(): JSX.Element {
    return (
        <Provider store={store}>
            <Suspense
                fallback={(
                    <div className="app-loading">
                        <Spinner size={SpinnerSize.large} />
                    </div>
                  )}
            >
                <GlobalErrorHandler
                    backgroundPicture="/assets/img/ErrorPopup.svg"
                    homePagePath="/home"
                    webAppName={ChannelTypes.ProfilePortal}
                    webAppVersion={appVersion ?? ''}
                    traceClientApiConfig={{
                        authService,
                        apiGatewayUrl: publicPortalUrl,
                        apiVersion: 1,
                    }}
                >
                    <BrowserRouter>
                        <Switch>
                            <Route
                                path="/acceptInvitation/:id?"
                                component={AcceptInvitation}
                            />
                            <Route path="/verifyIntermediary" />
                            <Route
                                exact
                                path="/index"
                                component={AnonymousHome}
                            />
                            <Route exact path="/signUp" component={SignUp} />
                            <Route
                                path="/intermediaries/:taxpayerId?/contactdetails/:type?/validation/:otp?"
                                component={VerifyIntermediary}
                            />
                            <Route>
                                <CheckAuthentication
                                    authService={authService}
                                    applicationName={APP_NAME}
                                    applicationLogo={APP_LOGO}
                                >
                                    {' '}
                                    <Switch>
                                        <Route
                                            path="/login"
                                            exact
                                            render={() => (
                                                <LoginCallback
                                                    languageFromToken
                                                    authService={authService}
                                                    applicationName={APP_NAME}
                                                    applicationLogo={APP_LOGO}
                                                />
                                            )}
                                        />
                                        <Route
                                            path="/switchTaxpayer"
                                            exact
                                            render={() => (
                                                <SwitchTaxpayer
                                                    authService={authService}
                                                    applicationLogo={APP_LOGO}
                                                />
                                            )}
                                        />
                                        <Route
                                            path="/logout"
                                            exact
                                            render={() => (
                                                <LogoutCallback
                                                    authService={authService}
                                                    applicationName={APP_NAME}
                                                    applicationLogo={APP_LOGO}
                                                />
                                            )}
                                        />
                                        <Route
                                            path="/changePassword"
                                            exact
                                            render={() => (
                                                <ChangePassword
                                                    authService={authService}
                                                    applicationLogo={APP_LOGO}
                                                />
                                            )}
                                        />
                                        <Route
                                            path="/silentLogin"
                                            exact
                                            render={() => (
                                                <SilentLoginCallback
                                                    authService={authService}
                                                />
                                            )}
                                        />
                                        <Route>
                                            <Layout menuItems={menuItems}>
                                                <Switch>
                                                    <Route
                                                        path="/error/:id?"
                                                        exact
                                                        // eslint-disable-next-line react/jsx-props-no-spreading
                                                        render={(props) => (
                                                            <ErrorPage
                                                                // eslint-disable-next-line react/jsx-props-no-spreading
                                                                {...props}
                                                            />
                                                        )}
                                                    />
                                                    <Route
                                                        path="/MyProfile"
                                                        exact
                                                        component={MyProfile}
                                                    />
                                                    <Route
                                                        path="/home"
                                                        exact
                                                        render={() => {
                                                            return (
                                                                <Redirect to="/myProfile" />
                                                            );
                                                        }}
                                                    />
                                                    <Route
                                                        path="/"
                                                        exact
                                                        render={() => {
                                                            return (
                                                                <Redirect to="/myProfile" />
                                                            );
                                                        }}
                                                    />
                                                    <Route
                                                        path="/codeManagement"
                                                        exact
                                                        component={
                                                            CodeManagement
                                                        }
                                                    />
                                                    <Route
                                                        path={[
                                                            '/TaxpayerProfile',
                                                            '/TaxpayerProfile?ClientId=:ClientId',
                                                        ]}
                                                        exact
                                                        component={
                                                            TaxpayerProfile
                                                        }
                                                    />
                                                    <Route
                                                        path="/TaxpayerProfile/RegisterIntermediary"
                                                        exact
                                                        component={Application}
                                                    />
                                                    <SecuredRoute
                                                        path="/TaxpayerProfile/pos/devices/:id"
                                                        exact
                                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                        render={(props: any) =>
                                                            !ClaimsHelper.isB2cTagCanMngPos() ||
                                                            (props.match.params
                                                                .id === 'new' &&
                                                                !registerPOSConfig) ? (
                                                                // eslint-disable-next-line react/jsx-props-no-spreading
                                                                <ErrorPage
                                                                    customId="403"
                                                                    {...props}
                                                                />
                                                            ) : (
                                                                <DeviceDetails />
                                                            )
                                                        }
                                                    />
                                                    <Route
                                                        path="/userguide"
                                                        exact
                                                        component={() => {
                                                            window.location.href = `${EnsureNotSlashAtEnd(
                                                                invoicingPortalURL
                                                            )}/content?lng=${
                                                                i18n.language
                                                            }&rin=${
                                                                localStorage.getItem(
                                                                    'RIN'
                                                                ) ?? ''
                                                            }`;
                                                            return null;
                                                        }}
                                                    />
                                                    <Route
                                                        path="/invoicePortal"
                                                        exact
                                                        component={() => {
                                                            window.location.href = `${EnsureNotSlashAtEnd(
                                                                invoicingPortalURL
                                                            )}/home?lng=${
                                                                i18n.language
                                                            }&rin=${
                                                                localStorage.getItem(
                                                                    'RIN'
                                                                ) ?? ''
                                                            }`;
                                                            return null;
                                                        }}
                                                    />
                                                    <Route
                                                        path="/*"
                                                        component={ErrorPage}
                                                    />
                                                </Switch>
                                            </Layout>
                                        </Route>
                                    </Switch>
                                </CheckAuthentication>
                            </Route>
                            <Route path="/*" component={ErrorPage} />
                        </Switch>
                    </BrowserRouter>
                </GlobalErrorHandler>
            </Suspense>
        </Provider>
    );
}

export default App;
